import React, { useEffect, useState, useCallback } from 'react';
import { func, shape } from 'prop-types';
import { connect } from 'react-redux';
import {
  IntlProvider,
  FormattedMessage,
  FormattedDate,
  FormattedDateTimeRange,
  createIntl,
  createIntlCache,
  injectIntl,
  useIntl,
} from 'react-intl';
// Note: we import React Intl directly from dist directory, because
// by default the library assumes the usage of ES modules
// and that don't work with server-side rendering.
// https://github.com/formatjs/formatjs/issues/1499#issuecomment-570151879

// Import translations  
import en from '../translations/en.json';
import sv from '../translations/sv.json'; 
import es from '../translations/es.json';
import fr from '../translations/fr.json';
import de from '../translations/de.json';
import it from '../translations/it.json';

// Import API helper for hosted translations
import { fetchHostedTranslations as fetchHostedTranslationsAPI } from './api';

const messages = { en, sv, es, fr, de, it };

// Client-side cache for translated hosted messages (30 minutes TTL)
const hostedMessagesCache = new Map();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Fetch translated hosted messages from the server
 * @param {string} locale - Target locale (e.g., 'es', 'fr')
 * @returns {Promise<Object>} - Translated messages object
 */
const fetchTranslatedHostedMessages = async (locale) => {
  const cacheKey = `hosted-messages-${locale}`;
  
  // Check cache first
  const cached = hostedMessagesCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    console.log(`✅ Using cached hosted messages for locale: ${locale}`);
    return cached.data;
  }
  
  try {
    console.log(`🔄 Fetching translated hosted messages for locale: ${locale}`);
    const data = await fetchHostedTranslationsAPI(locale);
    const translatedMessages = data.data || {};
    
    // Cache the result
    hostedMessagesCache.set(cacheKey, {
      data: translatedMessages,
      timestamp: Date.now()
    });
    
    console.log(`✅ Successfully fetched ${Object.keys(translatedMessages).length} hosted messages for locale: ${locale}`);
    return translatedMessages;
  } catch (error) {
    console.error(`❌ Error fetching hosted translations for ${locale}:`, error);
    return {};
  }
};

const intlShape = shape({
  formatDate: func.isRequired,
  formatDateTimeRange: func.isRequired,
  formatMessage: func.isRequired,
  formatNumber: func.isRequired,
  formatPlural: func.isRequired,
  formatRelativeTime: func.isRequired,
  formatTime: func.isRequired,
});

// Redux-connected IntlProvider component with hosted translations support
const ReduxConnectedIntlProviderComponent = props => {
  const { children, language, hostedMessages, ...rest } = props;
  const [translatedHostedMessages, setTranslatedHostedMessages] = useState({});
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  
  // Map language to locale format expected by react-intl
  const locale = language === 'en' ? 'en-US' : 
                 language === 'sv' ? 'sv-SE' : 
                 language === 'es' ? 'es-ES' : 
                 language === 'fr' ? 'fr-FR' : 
                 language === 'de' ? 'de-DE' : 
                 language === 'it' ? 'it-IT' : 'en-US'; // Added Italian locale mapping
  
  const baseMessages = messages[language] || messages.en;
  const englishMessages = messages.en;
  
  // Fetch translated hosted messages when language changes
  useEffect(() => {
    // Only fetch translations for non-English languages
    if (language && language !== 'en') {
      setIsLoadingTranslations(true);
      
      fetchTranslatedHostedMessages(language)
        .then(translatedMessages => {
          setTranslatedHostedMessages(translatedMessages);
          setIsLoadingTranslations(false);
        })
        .catch(error => {
          console.error('Failed to load translated hosted messages:', error);
          setIsLoadingTranslations(false);
          setTranslatedHostedMessages({}); // Clear on error
        });
    } else {
      // For English, clear any existing translations and stop loading
      setTranslatedHostedMessages({});
      setIsLoadingTranslations(false);
    }
  }, [language]);
  
  // Merge all message sources with correct priority order (lowest to highest):
  // 1. English base (en.json) - fallback for missing keys in other languages
  // 2. Language-specific base messages - from local JSON files
  // 3. Hosted messages - from Sharetribe Console
  // 4. Translated hosted messages - translated versions of hosted messages
  // 5. LOCAL OVERRIDES (highest priority) - from local language JSON files
  // This ensures NO translation key ever renders as literal text - always falls back to English
  
  // Import local glossary overrides for the current language
  // These have the HIGHEST priority to override hosted translations
  const getLocalGlossaryOverrides = () => {
    // Swedish glossary overrides and local strings
    if (language === 'sv') {
      return {
        'Glossary.listing': 'annons',
        'Glossary.listings': 'annonser',
        'Glossary.experience': 'upplevelse',
        'Glossary.experiences': 'upplevelser',
        'Glossary.listingType': 'annonstyp',
        'Glossary.classes': 'kurser',
        'Glossary.events': 'event',
        'Glossary.pickYourOwn': 'självplock',
        'Glossary.maintenance': 'skötsel',
        'Glossary.select': 'Välja',
      };
    }
    // English glossary overrides and local strings
    if (language === 'en') {
      return {
        'Glossary.select': 'Select',
      };
    }
    // German glossary overrides and local strings
    if (language === 'de') {
      return {
        'Glossary.select': 'Wählen',
      };
    }
    // Spanish glossary overrides and local strings
    if (language === 'es') {
      return {
        'Glossary.select': 'Seleccionar',
      };
    }
    // French glossary overrides and local strings
    if (language === 'fr') {
      return {
        'Glossary.select': 'Sélectionner',
      };
    }
    // Italian glossary overrides and local strings
    if (language === 'it') {
      return {
        'Glossary.select': 'Selezionare',
      };
    }
    return {};
  };
  
  const localOverrides = getLocalGlossaryOverrides();
  
  // Build message catalog: English fallback ensures NO missing keys render as literal text
  const localeMessages = language === 'en'
    ? {
        ...englishMessages, // English base messages
        ...baseMessages, // English base messages (same as above)
        ...localOverrides, // Apply local overrides (highest priority)
      }
    : {
        ...englishMessages, // ALWAYS include English as fallback - prevents literal key rendering
        ...baseMessages, // Language-specific local messages
        ...(hostedMessages || {}), // Swedish hosted messages (original)
        ...(translatedHostedMessages || {}), // Translated to target language
        ...localOverrides, // LOCAL OVERRIDES - HIGHEST PRIORITY (applies last, overrides all others)
      };

  // Debug: Monitor language changes and translation loading
  useEffect(() => {
    console.log('🌐 ReduxConnectedIntlProvider language changed:', language);
    console.log('🌐 Locale:', locale);
    console.log('🌐 Base messages count:', Object.keys(baseMessages).length);
    console.log('🌐 Hosted messages count:', Object.keys(hostedMessages || {}).length);
    console.log('🌐 Translated hosted messages count:', Object.keys(translatedHostedMessages).length);
    console.log('🌐 Total messages count:', Object.keys(localeMessages).length);
    
    if (isLoadingTranslations) {
      console.log('⏳ Loading hosted translations...');
    }
  }, [language, locale, baseMessages, hostedMessages, translatedHostedMessages, isLoadingTranslations, localeMessages]);

  return (
    <IntlProvider {...rest} locale={locale} messages={localeMessages}>
      {children}
    </IntlProvider>
  );
};

const mapStateToProps = state => {
  const { language } = state.ui;
  return {
    language: language || 'sv', // Default to Swedish for ShareGardens
  };
};

const ReduxConnectedIntlProvider = connect(mapStateToProps)(
  ReduxConnectedIntlProviderComponent
);

export {
  IntlProvider,
  FormattedMessage,
  FormattedDate,
  FormattedDateTimeRange,
  createIntl,
  createIntlCache,
  injectIntl,
  useIntl,
  intlShape,
  ReduxConnectedIntlProvider,
};
